/**
 * API client for the tenant-configurable payment-terms catalog.
 * Accounting owns the endpoints; sales/procurement consume read-only.
 */
import { apiGet, apiPost, apiPut, apiDelete } from '../api/axiosInstance';
import type { PaymentTerm, PaymentTermCreate, PaymentTermUpdate } from './types';

const BASE = '/api/v1/accounting/payment-terms';

interface DataResponse<T> {
  data: T;
}

/**
 * Document event a staged-term line's clock starts from. Catalog-driven on the
 * backend (accounting_payment_term_lines.trigger); the shared client mirrors the
 * current value set so callers can't send an unknown trigger.
 */
export type PaymentTermLineTrigger = 'on_order' | 'on_shipment' | 'on_receipt' | 'net' | 'eom';

/**
 * One installment line when persisting a staged term (L1). `proportion` is a
 * fraction of the document total (0.30 = 30%); proportion-based lines sum to 1.0.
 */
export interface PaymentTermLineInput {
  proportion?: number | null;
  fixed_amount?: number | null;
  trigger: PaymentTermLineTrigger;
  net_days: number;
  description?: string | null;
  /** Structured early-pay discount (L4c): earlier offset + rate; set both or neither. */
  discount_days?: number | null;
  discount_percent?: number | null;
}

export const paymentTermsApi = {
  list: async (isActive?: boolean): Promise<PaymentTerm[]> => {
    const qs = isActive === undefined ? '' : `?is_active=${isActive}`;
    const res = await apiGet<DataResponse<PaymentTerm[]>>(`${BASE}${qs}`);
    return res.data;
  },

  create: async (data: PaymentTermCreate): Promise<PaymentTerm> => {
    const res = await apiPost<DataResponse<PaymentTerm>>(BASE, data);
    return res.data;
  },

  update: async (id: string, data: PaymentTermUpdate): Promise<PaymentTerm> => {
    const res = await apiPut<DataResponse<PaymentTerm>>(`${BASE}/${id}`, data);
    return res.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiDelete(`${BASE}/${id}`);
  },

  /**
   * Atomically replace a term's installment schedule (staged-payment lines).
   * An empty array clears the schedule. Returns the updated term with lines.
   */
  replaceLines: async (id: string, lines: PaymentTermLineInput[]): Promise<PaymentTerm> => {
    const res = await apiPut<DataResponse<PaymentTerm>>(`${BASE}/${id}/lines`, { lines });
    return res.data;
  },
};
